import { Language } from '@eversports/language'
import { ImmutableMap } from '@eversports/maps'

interface LocaleSettingsOptions<L extends Language, C extends CountryCode> {
  language: L
  countryCode: C
  currency: string
  currencyCode: string
  defaultFacilityCurrency: string
}

export type CountryCode = 'AT' | 'DE' | 'CH' | 'NL' | 'ES' | 'GB' | 'FR' | 'ZA' | 'IT' | 'BE' | 'US'
export type Locale = (typeof allLocaleSettings)[number]['locale']

export class LocaleSetting<L extends Language = Language, C extends CountryCode = CountryCode> {
  public language: Language
  public countryCode: CountryCode
  public currency: string
  public locale: `${L}-${C}`
  public defaultFacilityCurrency: string
  public currencyCode: string

  constructor({ language, countryCode, currency, currencyCode, defaultFacilityCurrency }: LocaleSettingsOptions<L, C>) {
    this.language = language
    this.countryCode = countryCode
    this.currency = currency
    this.defaultFacilityCurrency = defaultFacilityCurrency
    this.locale = `${language}-${countryCode}`
    this.currencyCode = currencyCode
  }

  public toString() {
    return this.locale
  }
}

const allLocaleSettings = Object.freeze([
  // Austria
  new LocaleSetting({
    language: 'de',
    countryCode: 'AT',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: '€',
  }),

  // Germany
  new LocaleSetting({
    language: 'de',
    countryCode: 'DE',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: '€',
  }),

  // Switzerland
  new LocaleSetting({
    language: 'de',
    countryCode: 'CH',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: 'CHF',
  }),
  new LocaleSetting({
    language: 'fr',
    countryCode: 'CH',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: 'CHF',
  }),
  new LocaleSetting({
    language: 'it',
    countryCode: 'CH',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: 'CHF',
  }),

  // Netherlands
  new LocaleSetting({
    language: 'nl',
    countryCode: 'NL',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: '€',
  }),

  // Spain
  new LocaleSetting({
    language: 'es',
    countryCode: 'ES',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: '€',
  }),

  // Great Britain
  new LocaleSetting({
    language: 'en',
    countryCode: 'GB',
    currency: '£',
    currencyCode: 'GBP',
    defaultFacilityCurrency: '£',
  }),

  // United States
  new LocaleSetting({
    language: 'en',
    countryCode: 'US',
    currency: '$',
    currencyCode: 'USD',
    defaultFacilityCurrency: '$',
  }),

  // France
  new LocaleSetting({
    language: 'fr',
    countryCode: 'FR',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: '€',
  }),

  // Italy
  new LocaleSetting({
    language: 'it',
    countryCode: 'IT',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: '€',
  }),

  // Belgium
  new LocaleSetting({
    language: 'fr',
    countryCode: 'BE',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: '€',
  }),
  new LocaleSetting({
    language: 'nl',
    countryCode: 'BE',
    currency: '€',
    currencyCode: 'EUR',
    defaultFacilityCurrency: '€',
  }),
])

export const localeSettings = new ImmutableMap(
  allLocaleSettings.map((localeSetting) => [localeSetting.toString(), localeSetting]),
)
localeSettings.freeze()

export const DEFAULT_LOCALE = 'en-GB'
